import React, { useEffect, useState } from "react";

import {
  EXPERIMENT_REGISTERED_EVENT,
  HACKLE_DEVTOOLS_STORAGE_KEY,
  HACKLE_VARIATIONS,
  REGISTERED_EXPERIMENTS_KEY,
  getRegisteredExperiments,
} from "../../constants/hackle";
import { useIsDevEnvironment } from "../../hooks/useIsDevEnvironment";

type VariationMap = Record<number, string>;

/**
 * Hackle 변이 수동 토글 패널. platform goi_common GoiHackleDevtools와 동일 역할.
 *
 * dev/preview에서만 렌더된다(useIsDevEnvironment). 프로덕션 번들에는 컴포넌트가 남지만
 * 렌더 결과가 빈 fragment라 화면에는 아무것도 안 나온다.
 *
 * 목록은 useCustomVariation이 registerExperiment로 적재한 실험 key다. 즉 그 훅을 호출하는
 * 페이지를 한 번 방문해야 목록에 뜬다. 안 뜨면 "새로고침" 버튼.
 *
 * Chakra/MUI에 의존하지 않는다(인라인 스타일). 포털마다 Provider 구성이 달라서 어디에
 * 마운트해도 깨지지 않게 하려는 의도이므로 스타일 라이브러리로 바꾸지 말 것.
 */
export const GoiDevTools = () => {
  const isDevEnvironment = useIsDevEnvironment();

  if (!isDevEnvironment) return <></>;

  return <GoiHackleDevtools />;
};

const GoiHackleDevtools = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [variations, setVariations] = useState<VariationMap>({});
  const [registeredExperiments, setRegisteredExperiments] = useState<number[]>(
    []
  );
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const stored = window.localStorage.getItem(HACKLE_DEVTOOLS_STORAGE_KEY);
    if (stored) {
      try {
        setVariations(JSON.parse(stored));
      } catch {
        setVariations({});
      }
    }

    setRegisteredExperiments(getRegisteredExperiments());

    const handleNewExperiment = () => {
      setRegisteredExperiments(getRegisteredExperiments());
    };

    window.addEventListener(EXPERIMENT_REGISTERED_EVENT, handleNewExperiment);

    return () => {
      window.removeEventListener(
        EXPERIMENT_REGISTERED_EVENT,
        handleNewExperiment
      );
    };
  }, []);

  const saveVariations = (newVariations: VariationMap) => {
    window.localStorage.setItem(
      HACKLE_DEVTOOLS_STORAGE_KEY,
      JSON.stringify(newVariations)
    );
    setVariations(newVariations);
    setHasChanges(true);
  };

  const handleVariationChange = (experimentKey: number, variation: string) => {
    saveVariations({ ...variations, [experimentKey]: variation });
  };

  const handleClearExperiment = (experimentKey: number) => {
    saveVariations(
      Object.fromEntries(
        Object.entries(variations).filter(
          ([key]) => Number(key) !== experimentKey
        )
      )
    );
  };

  const handleClearAll = () => {
    window.localStorage.removeItem(HACKLE_DEVTOOLS_STORAGE_KEY);
    window.localStorage.removeItem(REGISTERED_EXPERIMENTS_KEY);
    setVariations({});
    setRegisteredExperiments([]);
    setHasChanges(true);
  };

  // 변이 오버라이드는 useVariation 호출 시점에 읽히므로 리로드가 필요하다.
  const handleReload = () => {
    window.location.reload();
  };

  const handleRefreshExperiments = () => {
    setRegisteredExperiments(getRegisteredExperiments());
  };

  if (!isOpen) {
    return (
      <>
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: "fixed",
            bottom: "30%",
            right: 16,
            zIndex: 9999,
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: hasChanges ? "#f59e0b" : "#7860ff",
            border: "none",
            cursor: "pointer",
            fontSize: 20,
            boxShadow: "0 4px 16px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          {hasChanges ? "R" : "AB"}
        </button>
      </>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        top: "50%",
        right: 16,
        transform: "translateY(-50%)",
        zIndex: 9999,
        background: "#1a1a1a",
        borderRadius: 12,
        boxShadow: "0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)",
        width: 300,
        maxHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 16,
          borderBottom: "1px solid #333",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
            Hackle Devtools
          </span>
          <button
            onClick={handleRefreshExperiments}
            style={{
              background: "none",
              border: "none",
              color: "#4ade80",
              cursor: "pointer",
              fontSize: 12,
            }}
            title="실험 목록 새로고침"
          >
            <u>새로고침</u>
          </button>
        </div>

        <button
          onClick={() => setIsOpen(false)}
          style={{
            background: "none",
            border: "none",
            color: "#888",
            cursor: "pointer",
            fontSize: 18,
          }}
        >
          x
        </button>
      </div>

      <div
        style={{ color: "white", padding: "0px 16px 8px 16px", fontSize: 12 }}
      >
        실험이 보이지 않으면 해당 페이지를 한 번 방문한 뒤 &quot;새로고침&quot;을
        눌러주세요
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {hasChanges && (
          <div
            style={{
              background: "#fbbf24",
              color: "#000",
              padding: "8px 12px",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            변경사항을 적용하려면 &quot;적용하기&quot;를 눌러주세요
          </div>
        )}

        {registeredExperiments.length === 0 ? (
          <div
            style={{
              color: "#666",
              fontSize: 13,
              textAlign: "center",
              padding: 16,
            }}
          >
            감지된 실험이 없습니다
            <br />
            <span style={{ fontSize: 11 }}>
              useCustomVariation을 쓰는 페이지를 방문해주세요
            </span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {registeredExperiments.map((experimentKey) => (
              <div
                key={experimentKey}
                style={{ background: "#2a2a2a", borderRadius: 6, padding: 12 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{ color: "#4ade80", fontWeight: 600, fontSize: 13 }}
                  >
                    Exp #{experimentKey}
                  </span>
                  {variations[experimentKey] && (
                    <button
                      onClick={() => handleClearExperiment(experimentKey)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#f87171",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      Reset
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {HACKLE_VARIATIONS.map((v) => (
                    <button
                      key={v}
                      onClick={() => handleVariationChange(experimentKey, v)}
                      style={{
                        flex: 1,
                        padding: "6px 8px",
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                        background:
                          variations[experimentKey] === v ? "#4ade80" : "#444",
                        color:
                          variations[experimentKey] === v ? "#000" : "#fff",
                        fontWeight: 500,
                        fontSize: 12,
                      }}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                {!variations[experimentKey] && (
                  <div style={{ color: "#888", fontSize: 11, marginTop: 6 }}>
                    SDK 배정값 사용 중
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          padding: 16,
          borderTop: "1px solid #333",
          background: "#1a1a1a",
          flexShrink: 0,
        }}
      >
        <button
          onClick={handleClearAll}
          style={{
            flex: 1,
            padding: "10px 12px",
            border: "1px solid #555",
            borderRadius: 6,
            cursor: "pointer",
            background: "transparent",
            color: "#888",
            fontWeight: 500,
          }}
        >
          전체 초기화
        </button>
        <button
          onClick={handleReload}
          style={{
            flex: 1,
            padding: "10px 12px",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            background: hasChanges ? "#f59e0b" : "#3b82f6",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          적용하기
        </button>
      </div>
    </div>
  );
};
